import _ from 'lodash';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';

import CancelIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Check';

import EnhancedTableHead from '../../components/EnhancedTableHead';
import FMPHeadline from '../../components/ui/FMPHeadline';
import AuthUserContainer from '../../containers/AuthUser';
import AssignedLoadingNumbersContainer from '../../containers/logistics/AssignedLoadingNumbers';
import StationsContainer from '../../containers/Stations';
import TerminalsContainer from '../../containers/logistics/Terminals';
import { isFiltered, sortStationsByNumber } from '../../libs/utility';
import { getSharedStyles } from '../../themes';

const DEFAULT_TERMINAL_ID = 9; // Petro-Diamond (PDI)
const isLocalhost = Boolean(window.location.hostname === 'localhost');

const FUEL_TYPES = [
	{ fuelType: 'unl', label: 'UNL' },
	{ fuelType: 'preunl', label: 'Premium' },
	{ fuelType: 'etoh', label: 'Ethanol' },
	{ fuelType: 'e85', label: 'E85' },
];

const styles = (theme) => ({
	...getSharedStyles(theme),
	toolbar: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'flex-end',
		padding: '10px 10px 20px 10px',
		borderBottom: '1px solid #ddd',
		marginBottom: 10,
	},
	terminalSelect: {
		minWidth: 320,
		marginRight: 30,
	},
	toolbarButtons: {
		flex: 1,
		textAlign: 'right',
	},
	button: {
		borderRadius: 100,
		marginLeft: 10,
		marginTop: 10,
	},
	stationNumber: {
		fontWeight: 600,
		whiteSpace: 'nowrap',
	},
	stationName: {
		color: '#777',
		fontSize: 12,
	},
	loadingNumber: {
		fontFamily: 'monospace',
		fontSize: 14,
	},
	missing: {
		color: '#bbb',
	},
	editField: {
		width: 110,
	},
	stationSelect: {
		minWidth: 320,
	},
	meta: {
		color: '#999',
		fontSize: 12,
		whiteSpace: 'nowrap',
	},
	summary: {
		padding: '10px 10px 0px 10px',
		color: '#777',
		fontSize: 13,
	},
	empty: {
		padding: 40,
		textAlign: 'center',
		color: '#999',
		fontSize: 18,
		fontWeight: 300,
	},
});

const columnData = [
	{ id: 'number', label: 'Station', padding: 'checkbox' },
	{ id: 'unl', label: 'UNL', padding: 'checkbox' },
	{ id: 'preunl', label: 'Premium', padding: 'checkbox' },
	{ id: 'etoh', label: 'Ethanol', padding: 'checkbox' },
	{ id: 'e85', label: 'E85', padding: 'checkbox' },
	{ id: 'updatedSort', label: 'Updated', padding: 'checkbox' },
	{ id: 'actions', label: '\u00a0', padding: 'checkbox' },
];

function emptyValues() {
	return { unl: '', preunl: '', etoh: '', e85: '' };
}

class AssignedLoadingNumbers extends Component {
	constructor(props) {
		super(props);
		const terminalId = _.get(props, ['match', 'params', 'terminalId']);
		this.state = {
			terminalId: terminalId ? parseInt(terminalId, 10) : '',
			isLoading: false,
			search: '',
			order: 'asc',
			orderBy: 'number',

			editingStationId: null,
			editValues: emptyValues(),
			isSaving: false,

			isAdding: false,
			addStationId: '',
			addValues: emptyValues(),

			confirmRemoveStationId: null,
		};
	}

	componentDidMount() {
		const { terminals, searchTerminals, stations, searchStations } = this.props;
		if (!terminals.length) {
			searchTerminals();
		}
		if (!stations.length) {
			searchStations();
		}
		if (this.state.terminalId) {
			this.load(this.state.terminalId);
		} else {
			//- Default to Petro-Diamond, the terminal this was built for
			this.props.history.replace(`/logistics/loadingAssignments/assigned/${DEFAULT_TERMINAL_ID}`);
		}
	}

	componentDidUpdate(prevProps) {
		const prev = _.get(prevProps, ['match', 'params', 'terminalId']);
		const cur = _.get(this.props, ['match', 'params', 'terminalId']);
		if (prev !== cur) {
			const terminalId = cur ? parseInt(cur, 10) : '';
			this.setState({
				terminalId,
				editingStationId: null,
				isAdding: false,
			});
			if (terminalId) {
				this.load(terminalId);
			}
		}
	}

	load = (terminalId) => {
		this.setState({ isLoading: true });
		this.props.searchAssignedLoadingNumbers({
			params: { terminalId },
			callback: () => {
				this.setState({ isLoading: false });
			},
		});
	};

	handleTerminalChange = (event) => {
		const terminalId = event.target.value;
		this.props.history.push(`/logistics/loadingAssignments/assigned/${terminalId}`);
	};

	handleSearch = (event) => {
		this.setState({ search: event.target.value });
	};

	handleRequestSort = (orderBy) => {
		let order = 'desc';
		if (this.state.orderBy === orderBy && this.state.order === 'desc') {
			order = 'asc';
		}
		this.setState({ order, orderBy });
	};

	//- Rows grouped by station -------------------------------------------------------------

	getStationRows() {
		const { assignedLoadingNumbers, stationMap, userMap } = this.props;
		const { terminalId } = this.state;

		const byStation = {};
		assignedLoadingNumbers.forEach((row) => {
			if (row.terminalId !== terminalId) {
				return;
			}
			const station = stationMap[row.stationId] || {};
			if (!byStation[row.stationId]) {
				byStation[row.stationId] = {
					stationId: row.stationId,
					number: station.number || `#${row.stationId}`,
					name: station.name || 'Missing station',
					unl: '',
					preunl: '',
					etoh: '',
					e85: '',
					updatedSort: '',
					updatedDisplay: '',
					updatedBy: '',
					ids: {},
				};
			}
			const entry = byStation[row.stationId];
			entry[row.fuelType] = row.loadingNumber;
			entry.ids[row.fuelType] = row.assignedLoadingNumberId;
			const updated = row.dateUpdated || row.dateCreated;
			if (updated && updated > entry.updatedSort) {
				entry.updatedSort = updated;
				entry.updatedDisplay = moment(updated).format('M/D/YY h:mm a');
				const user = userMap[row.updatedByUserId || row.createdByUserId];
				entry.updatedBy = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
			}
		});
		return _.values(byStation);
	}

	sortRows(rows) {
		const { order, orderBy } = this.state;
		if (orderBy === 'number') {
			return sortStationsByNumber(rows, order);
		}
		return _.orderBy(
			rows,
			[(r) => (r[orderBy] === '' ? null : r[orderBy]), 'number'],
			[order, 'asc']
		);
	}

	//- Inline edit -------------------------------------------------------------------------

	startEdit = (row) => {
		this.setState({
			editingStationId: row.stationId,
			editValues: {
				unl: row.unl,
				preunl: row.preunl,
				etoh: row.etoh,
				e85: row.e85,
			},
			isAdding: false,
		});
	};

	cancelEdit = () => {
		this.setState({ editingStationId: null, editValues: emptyValues() });
	};

	onEditValueChange = (field, value, stateKey = 'editValues') => {
		this.setState({ [stateKey]: { ...this.state[stateKey], [field]: value } });
	};

	saveStation = ({ stationId, values, afterSave }) => {
		const { saveAssignedLoadingNumbers, showErrors } = this.props;
		const { terminalId } = this.state;

		const fuelTypes = FUEL_TYPES.map(({ fuelType }) => ({
			fuelType,
			loadingNumber: (values[fuelType] || '').trim(),
		}));

		if (!fuelTypes.some((f) => f.loadingNumber)) {
			return showErrors(['Enter at least one loading number']);
		}

		this.setState({ isSaving: true });
		saveAssignedLoadingNumbers({
			params: { terminalId, stationId, fuelTypes },
			callback: (err) => {
				this.setState({ isSaving: false });
				if (err) {
					return;
				}
				afterSave();
				this.load(terminalId);
			},
		});
	};

	saveEdit = () => {
		const { editingStationId, editValues } = this.state;
		this.saveStation({
			stationId: editingStationId,
			values: editValues,
			afterSave: () => this.cancelEdit(),
		});
	};

	//- Add station -------------------------------------------------------------------------

	startAdd = () => {
		this.setState({
			isAdding: true,
			addStationId: '',
			addValues: emptyValues(),
			editingStationId: null,
		});
	};

	cancelAdd = () => {
		this.setState({ isAdding: false, addStationId: '', addValues: emptyValues() });
	};

	saveAdd = () => {
		const { addStationId, addValues } = this.state;
		if (!addStationId) {
			return this.props.showErrors(['Pick a station first']);
		}
		this.saveStation({
			stationId: addStationId,
			values: addValues,
			afterSave: () => this.cancelAdd(),
		});
	};

	//- Remove ------------------------------------------------------------------------------

	confirmRemove = () => {
		const { removeAssignedLoadingNumbers } = this.props;
		const { terminalId, confirmRemoveStationId } = this.state;
		removeAssignedLoadingNumbers({
			params: { terminalId, stationId: confirmRemoveStationId },
			callback: (err) => {
				this.setState({ confirmRemoveStationId: null });
				if (!err) {
					this.load(terminalId);
				}
			},
		});
	};

	//- Render ------------------------------------------------------------------------------

	getExportUrl() {
		const { terminalId } = this.state;
		const request = {
			method: 'assignedLoadingNumber.export',
			params: { terminalId, downloadCsv: true },
		};
		const host = isLocalhost ? 'http://localhost:3700' : '';
		return `${host}/api?request=${encodeURIComponent(JSON.stringify(request))}`;
	}

	renderTerminalSelect() {
		const { classes, terminals } = this.props;
		const { terminalId } = this.state;
		//- Only terminals with "Use assigned loading numbers" checked on a supplier product
		const options = _.sortBy(
			terminals.filter((t) => t && _.some(t.supplierProducts, 'useAssignedLoadingNumbers')),
			'name'
		);
		return (
			<FormControl className={classes.terminalSelect}>
				<InputLabel>Terminal</InputLabel>
				<Select value={terminalId} onChange={this.handleTerminalChange} input={<Input />}>
					{options.map((terminal) => (
						<MenuItem key={terminal.terminalId} value={terminal.terminalId}>
							{terminal.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		);
	}

	renderValueFields({ values, stateKey, disabled }) {
		const { classes } = this.props;
		return (
			<Fragment>
				{FUEL_TYPES.map(({ fuelType, label }) => (
					<TableCell key={fuelType}>
						<TextField
							className={classes.editField}
							placeholder={label}
							value={values[fuelType]}
							disabled={disabled}
							onChange={(e) => this.onEditValueChange(fuelType, e.target.value, stateKey)}
							inputProps={{ style: { fontFamily: 'monospace' } }}
						/>
					</TableCell>
				))}
			</Fragment>
		);
	}

	renderAddRow(existingStationIds) {
		const { classes, stations } = this.props;
		const { addStationId, addValues, isSaving } = this.state;
		const available = sortStationsByNumber(
			stations.filter((s) => s && !s.isHidden && !existingStationIds[s.stationId])
		);
		return (
			<TableRow className={classes.row}>
				<TableCell>
					<FormControl className={classes.stationSelect}>
						<InputLabel>Station</InputLabel>
						<Select
							value={addStationId}
							onChange={(e) => this.setState({ addStationId: e.target.value })}
							input={<Input />}
						>
							{available.map((station) => (
								<MenuItem key={station.stationId} value={station.stationId}>
									{station.number} - {station.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</TableCell>
				{this.renderValueFields({ values: addValues, stateKey: 'addValues', disabled: isSaving })}
				<TableCell />
				<TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
					<Tooltip title="Save">
						<IconButton color="primary" onClick={this.saveAdd} disabled={isSaving}>
							<SaveIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Cancel">
						<IconButton onClick={this.cancelAdd} disabled={isSaving}>
							<CancelIcon />
						</IconButton>
					</Tooltip>
				</TableCell>
			</TableRow>
		);
	}

	renderRow(row) {
		const { classes, canMakeUpdates } = this.props;
		const { editingStationId, editValues, isSaving } = this.state;
		const isEditing = editingStationId === row.stationId;

		return (
			<TableRow hover className={classes.row} key={row.stationId}>
				<TableCell>
					<div className={classes.stationNumber}>{row.number}</div>
					<div className={classes.stationName}>{row.name}</div>
				</TableCell>

				{isEditing &&
					this.renderValueFields({
						values: editValues,
						stateKey: 'editValues',
						disabled: isSaving,
					})}

				{!isEditing &&
					FUEL_TYPES.map(({ fuelType }) => (
						<TableCell key={fuelType} className={classes.loadingNumber}>
							{row[fuelType] || <span className={classes.missing}>—</span>}
						</TableCell>
					))}

				<TableCell className={classes.meta}>
					<div>{row.updatedDisplay}</div>
					<div>{row.updatedBy}</div>
				</TableCell>

				<TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
					{isEditing && (
						<Fragment>
							<Tooltip title="Save">
								<IconButton color="primary" onClick={this.saveEdit} disabled={isSaving}>
									<SaveIcon />
								</IconButton>
							</Tooltip>
							<Tooltip title="Cancel">
								<IconButton onClick={this.cancelEdit} disabled={isSaving}>
									<CancelIcon />
								</IconButton>
							</Tooltip>
						</Fragment>
					)}
					{!isEditing && canMakeUpdates && (
						<Fragment>
							<Tooltip title="Edit">
								<IconButton onClick={() => this.startEdit(row)}>
									<EditIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Remove station from this terminal">
								<IconButton
									onClick={() => this.setState({ confirmRemoveStationId: row.stationId })}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Fragment>
					)}
				</TableCell>
			</TableRow>
		);
	}

	renderRemoveDialog() {
		const { stationMap } = this.props;
		const { confirmRemoveStationId } = this.state;
		const station = stationMap[confirmRemoveStationId] || {};
		return (
			<Dialog
				open={!!confirmRemoveStationId}
				onClose={() => this.setState({ confirmRemoveStationId: null })}
			>
				<DialogTitle>Remove {station.number}?</DialogTitle>
				<DialogContent>
					This removes every assigned loading number for {station.number} {station.name} at this
					terminal. Orders for this station will warn that no loading number is on file.
				</DialogContent>
				<DialogActions>
					<Button onClick={() => this.setState({ confirmRemoveStationId: null })}>Cancel</Button>
					<Button variant="contained" color="secondary" onClick={this.confirmRemove}>
						Remove
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	render() {
		const { classes, canMakeUpdates, terminalMap, isExternalLogistics } = this.props;
		const { terminalId, search, order, orderBy, isAdding, isLoading } = this.state;

		const canEdit = canMakeUpdates && !isExternalLogistics;
		const terminal = terminalMap[terminalId];

		const allRows = this.getStationRows();
		const existingStationIds = _.keyBy(allRows, 'stationId');
		let rows = allRows.filter((row) => {
			return !isFiltered({
				search,
				data: row,
				fields: ['number', 'name', 'unl', 'preunl', 'etoh', 'e85'],
			});
		});
		rows = this.sortRows(rows);

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<FMPHeadline
						label="Assigned Loading Numbers"
						value={search}
						onChange={this.handleSearch}
					/>

					<Paper className={classes.paper}>
						<div className={classes.toolbar}>
							{this.renderTerminalSelect()}

							{terminalId && (
								<div className={classes.toolbarButtons}>
									{canEdit && (
										<Button
											className={classes.button}
											variant="contained"
											color="primary"
											onClick={this.startAdd}
											disabled={isAdding}
										>
											ADD STATION
										</Button>
									)}
									<a href={this.getExportUrl()} style={{ textDecoration: 'none' }}>
										<Button
											className={classes.button}
											variant="outlined"
											disabled={!allRows.length}
										>
											EXPORT CSV
										</Button>
									</a>
								</div>
							)}
						</div>

						{!terminalId && (
							<div className={classes.empty}>
								Pick a terminal to manage its station loading numbers.
								<div style={{ fontSize: 14, marginTop: 20 }}>
									Looking for the one time use pool?{' '}
									<Link to="/logistics/loadingAssignments/pooled">Pooled Loading Numbers</Link>
								</div>
							</div>
						)}

						{terminalId && (
							<Fragment>
								<div className={classes.summary}>
									{terminal ? terminal.name : ''}: {allRows.length} station
									{allRows.length === 1 ? '' : 's'} with assigned loading numbers
									{isLoading ? ' (refreshing...)' : ''}
								</div>

								<Table stickyHeader size="small" className={classes.table}>
									<EnhancedTableHead
										columnData={columnData}
										order={order}
										orderBy={orderBy}
										onRequestSort={this.handleRequestSort}
									/>
									<TableBody>
										{isAdding && this.renderAddRow(existingStationIds)}
										{rows.map((row) => this.renderRow(row))}
									</TableBody>
								</Table>

								{!allRows.length && !isLoading && !isAdding && (
									<div className={classes.empty}>
										No assigned loading numbers for this terminal yet. Add a station to get started.
									</div>
								)}
							</Fragment>
						)}
					</Paper>
				</Grid>

				{this.renderRemoveDialog()}
			</Grid>
		);
	}
}

AssignedLoadingNumbers.propTypes = {
	classes: PropTypes.object.isRequired,
	history: PropTypes.object,
	match: PropTypes.object,

	assignedLoadingNumbers: PropTypes.array.isRequired,
	stationMap: PropTypes.object.isRequired,
	terminalMap: PropTypes.object.isRequired,
	userMap: PropTypes.object.isRequired,
	canMakeUpdates: PropTypes.bool,
	isExternalLogistics: PropTypes.bool,

	searchAssignedLoadingNumbers: PropTypes.func.isRequired,
	saveAssignedLoadingNumbers: PropTypes.func.isRequired,
	removeAssignedLoadingNumbers: PropTypes.func.isRequired,
	showErrors: PropTypes.func.isRequired,

	stations: PropTypes.array.isRequired,
	searchStations: PropTypes.func.isRequired,
	terminals: PropTypes.array.isRequired,
	searchTerminals: PropTypes.func.isRequired,
};

export default _.flow([
	withStyles(styles),
	AssignedLoadingNumbersContainer,
	StationsContainer,
	TerminalsContainer,
	AuthUserContainer,
])(AssignedLoadingNumbers);
